/**
 * A session that ends while the tab is open (expired, revoked, signed out elsewhere) turns every
 * API call into a 401. Before this, a signed-out tab kept polling and rendering errors — 367 such
 * 401s on one path in a week in production — instead of offering the one thing that helps.
 *
 * The rule: a 401 from any API call made while the user is inside the app sends the tab to
 * sign-in, carrying them back to where they were. `/api/auth/*` is exempt — those endpoints report
 * on the session rather than require one — and so is everything outside `/app`, where being
 * signed out is a normal state. One redirect per page load, however many calls fail together.
 */

interface Place {
  pathname: string
  search: string
  hash: string
}

export function signInUrlFor(place: Place): string {
  const back = `${place.pathname}${place.search}${place.hash}`
  return `/api/auth/sign-in?returnPathname=${encodeURIComponent(back)}`
}

/** Whether a 401 for this request, seen on this page, should send the tab to sign-in. */
export function shouldRedirectToSignIn(status: number, requestUrl: string, place: Place): boolean {
  if (status !== 401) return false
  if (!place.pathname.startsWith('/app')) return false
  // The base only resolves a relative URL; it is never fetched, and the scheme cannot change the path.
  const path = new URL(requestUrl, 'https://local').pathname
  return path.startsWith('/api/') && !path.startsWith('/api/auth/')
}

let redirecting = false

export function redirectOnUnauthorized(
  status: number,
  requestUrl: string,
  // Absent outside a browser — a unit test running a query function has no tab to send anywhere.
  win: Pick<Window, 'location'> | undefined = typeof window === 'undefined' ? undefined : window,
): void {
  if (!win || redirecting || !shouldRedirectToSignIn(status, requestUrl, win.location)) return
  redirecting = true
  win.location.assign(signInUrlFor(win.location))
}
